import { Button, Dialog, DialogContent, Avatar, Checkbox, FormControlLabel, TextField, IconButton, InputAdornment } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { PiArrowsClockwise, PiArrowRight } from "react-icons/pi";
import CloseIcon from '@mui/icons-material/Close';
import BadgeFulfillment from "elements/BadgeFulfillment";
import changeStatus from 'assets/img/changeStatus.svg'
import { useState, useEffect } from "react";
import { Dropdown, Form } from 'semantic-ui-react';
import { ApiRequest } from "GlobalFunctions";

interface ConfirmStatusChangeDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    loading: boolean;
    oldStatus: string;
    newStatus: string;
    onConfirm: (sendNotification?: boolean, shippingCarrierCode?: string, trackingNumber?: string, customCarrier?: string) => void;
    isShippo: boolean;
}

export default function ConfirmStatusChangeDialog(props: ConfirmStatusChangeDialogProps) {
    const { 
        open, 
        setOpen, 
        loading,
        oldStatus,
        newStatus,
        onConfirm,
        isShippo
    } = props;

    const [sendNotification, setSendNotification] = useState(false);
    const [shippingProvider, setShippingProvider] = useState('');
    const [customCarrier, setCustomCarrier] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [shippingProviderOptions, setShippingProviderOptions] = useState<any[]>([]);

    const iconStyle = {
        width: '24px',
        height: '24px',
        marginTop: '-.33em',
        borderRadius: '0',
        objectFit: 'contain' as 'contain'
    };

    console.log('shippingProvider', shippingProvider);

    // Load shipping carriers from API
    useEffect(() => {
        const loadShippingCarriers = async () => {
            await ApiRequest({
                method: 'GET',
                url: '/companies/orders/getShippingCarriers',
                setResponse: (response: any) => {
                    if (response.status === 'success' && response.data) {
                        const options = response.data.map((carrier: any) => ({
                            key: carrier.carrier_code,
                            value: carrier.carrier_code,
                            text: carrier.carrier_name,
                            image: { avatar: true, src: carrier.logo_url, style: iconStyle }
                        }));
                        setShippingProviderOptions(options);
                    }
                }
            });
        };

        loadShippingCarriers();
    }, []);

    useEffect(() => {
        if (open && (newStatus === 'SHIPPED' || newStatus === 'READY_FOR_PICK_UP')) {
            setSendNotification(false);
            setShippingProvider('');
            setCustomCarrier('');
            setTrackingNumber('');
        }
    }, [open, newStatus]);

    const handleConfirm = () => {
        if (newStatus === 'SHIPPED') {
            const customCarrierValue = shippingProvider === 'other' ? customCarrier : undefined;
            onConfirm(sendNotification, !isShippo ? shippingProvider : undefined, !isShippo ? trackingNumber : undefined, !isShippo ? customCarrierValue : undefined);
        } else if (newStatus === 'READY_FOR_PICK_UP') {
            onConfirm(sendNotification);
        } else {
            onConfirm();
        }
    };

    // Get the logo URL for the selected carrier
    const getCarrierLogo = () => {
        const selectedCarrier = shippingProviderOptions.find(option => option.value === shippingProvider);
        return selectedCarrier?.image?.src || '';
    };

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
        >
            <DialogContent style={{ padding: '0', maxWidth: '470px' }}>
                <div className="change_dialog_content" style={{ padding: '50px 50px 0px 50px' }}>
                    <div className="cancel_dialog_header">
                        <img src={changeStatus} alt="Change Status" style={{ width: 40, height: 40 }} />
                        <h3 className='heading_h3_dialog'>Confirm Change</h3>
                    </div>

                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: '20px',
                        margin: '30px 0',
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                                fontSize: '12px', 
                                color: '#B9B9BA', 
                                marginBottom: '8px',
                                fontWeight: '500'
                            }}>
                                Old
                            </div>
                            <BadgeFulfillment code={oldStatus} />
                        </div>

                        <div style={{ 
                            fontSize: '20px', 
                            color: '#B9B9BA',
                            marginTop: '20px'
                        }}>
                            <PiArrowRight size={20} />
                        </div>

                        <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                                fontSize: '12px', 
                                color: '#B9B9BA', 
                                marginBottom: '8px',
                                fontWeight: '500'
                            }}>
                                New
                            </div>
                            <BadgeFulfillment code={newStatus} />
                        </div>
                    </div>

                    <div style={{ 
                        fontSize: '14px', 
                        color: '#666666',
                        textAlign: 'center',
                        lineHeight: '1.5',
                        marginBottom: '30px'
                    }}>
                        Once confirmed, this update will be saved and any connected workflows will run automatically.
                    </div>
                </div>

                {newStatus === 'SHIPPED' && (
                    <>
                        {!isShippo && (
                            <div style={{
                                padding: '0 50px 20px 50px',
                            }}>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '15px'
                                }}>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            color: '#424242',
                                            marginBottom: '8px',
                                            fontWeight: '500'
                                        }}>
                                            Shipping Provider
                                        </label>
                                        {shippingProvider !== 'other' ? (
                                            <Dropdown
                                                placeholder='Select A Provider'
                                                fluid
                                                selection
                                                options={shippingProviderOptions}
                                                value={shippingProvider}
                                                onChange={(e, { value }) => {
                                                    setShippingProvider(value as string);
                                                    setCustomCarrier('');
                                                }}
                                                style={{
                                                    borderRadius: '5px',
                                                    borderColor: '#E0E0E0'
                                                }}
                                            />
                                        ) : (
                                            <TextField
                                                fullWidth
                                                placeholder='Enter Custom Carrier'
                                                value={customCarrier}
                                                onChange={(e) => setCustomCarrier(e.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Avatar
                                                                src={getCarrierLogo()}
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    borderRadius: 0,
                                                                    objectFit: 'contain'
                                                                }}
                                                            />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton 
                                                                onClick={() => {
                                                                    setShippingProvider('');
                                                                    setCustomCarrier('');
                                                                }}
                                                                size="small"
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '5px',
                                                        fontSize: '14px'
                                                    }
                                                }}
                                            />
                                        )}
                                    </div>
                                    
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            color: '#424242',
                                            marginBottom: '8px',
                                            fontWeight: '500'
                                        }}>
                                            Tracking Number
                                        </label>
                                        <TextField
                                            fullWidth
                                            placeholder='Enter Tracking Number'
                                            value={trackingNumber}
                                            onChange={(e) => setTrackingNumber(e.target.value)}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '5px',
                                                    fontSize: '14px'
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '0 50px 20px 50px',
                        }}>
                            <div style={{
                                backgroundColor: '#F9F9F9',
                                padding: '0 0px 0 15px',
                                borderRadius: '2px',
                            }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={sendNotification}
                                        onChange={(e) => setSendNotification(e.target.checked)}
                                        sx={{
                                            color: '#B9B9BA',
                                            '&.Mui-checked': {
                                                color: '#7963FF',
                                            },
                                        }}
                                    />
                                }
                                label="Send a notification email to the customer"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '14px',
                                        color: '#424242',
                                    }
                                }}
                            />
                            </div>
                        </div>
                    </>
                )}

                {newStatus === 'READY_FOR_PICK_UP' && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '0 50px 20px 50px',
                    }}>
                        <div style={{
                            backgroundColor: '#F9F9F9',
                            padding: '0 0px 0 15px',
                            borderRadius: '2px',
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={sendNotification}
                                    onChange={(e) => setSendNotification(e.target.checked)}
                                    sx={{
                                        color: '#B9B9BA',
                                        '&.Mui-checked': {
                                            color: '#7963FF',
                                        },
                                    }}
                                />
                            }
                            label="Send a notification email to the customer"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '14px',
                                    color: '#424242',
                                }
                            }}
                        />
                        </div>
                    </div>
                )}

                <div className="change_dialog_actions">
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => setOpen(false)}
                        sx={{
                            borderColor: '#B9B9B9',
                            borderRadius: '5px !important',
                            color: '#424242',
                            padding: '6px 10px !important',
                            marginRight: '10px'
                        }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        loading={loading}
                        sx={{
                            borderRadius: '5px !important',
                            padding: '6px 10px !important',
                            backgroundColor: '#7963FF',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#6952E6'
                            }
                        }}
                    >
                        Confirm
                    </LoadingButton>
                </div>
            </DialogContent>
        </Dialog>
    )
}
